import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { promisifyRoutine } from 'redux-saga-routines'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import AddIcon from '@mui/icons-material/Add'
import { toast } from 'react-toastify'
import { rememberHubReturn } from 'utils/hubReturnPath'
import { getUserBps } from 'features/bpList/ducks/selectors'
import ActionsAPI from 'services/ActionsAPI'
import GuruTip from 'features/guru/GuruTip'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import TasksRemedialsExplainer from 'features/taskManagement/TasksRemedialsExplainer'
import RemedialsReviewEntry from 'features/taskManagement/components/RemedialsReviewEntry'
import AddActionDialog from 'features/actions/components/AddActionDialog'
import ConvertToTaskDialog from 'features/actions/components/ConvertToTaskDialog'
import DismissActionDialog from 'features/actions/components/DismissActionDialog'
import CompleteActionDialog from 'features/actions/components/CompleteActionDialog'
import ActionDetailDrawer from 'features/actions/components/ActionDetailDrawer'
import BulkDismissDialog from 'features/actions/components/BulkDismissDialog'
import BulkConvertDialog from 'features/actions/components/BulkConvertDialog'
import {
  bulkUpdateActionsRoutine,
  getActionSectionRoutine,
  getActionStatsRoutine,
  getActionsRoutine,
  paramsKey,
  refreshActionsRoutine,
  selectActionSections,
  selectActions,
  selectActionsError,
  selectActionsLoading,
  selectActionsMeta,
  selectLastMutatedAction,
  selectMutationError,
  selectMutationLoading,
  selectRemedialRefreshedVersion,
  selectActionStatsFor,
  remedialViewCounts,
  severityLiveCounts,
  selectStatsError,
  selectStatsKey,
  updateActionRoutine,
} from 'features/actions/ducks'
import { EMPTY_SECTION, mergeAction } from 'features/actions/ducks/reducer'
import DashboardView from './DashboardView'
import EstateFilter from './EstateFilter'
import { InvolvesChip } from './FilterChips'
import { OverdueExplainer, RemedialsHelp, StageExplainer } from './Explainers'
import ScopePickers from './ScopePickers'
import TableView from './TableView'
import ViewSidebar from './ViewSidebar'
import {
  DASHBOARD,
  DASHBOARD_SECTIONS,
  FILTER_RESET,
  PAGE_SIZE,
  VIEW_RESET,
  buildListParams,
  buildSectionParams,
  buildStatsParams,
  nextSort,
  normaliseLegacyParams,
  readFilters,
  readSort,
  readView,
  viewDef,
  withParams,
} from './remedialsQuery'
import { INK, MUTED, PrimaryButton } from './ui'

const HUB_RAIL_TOP = '158px'
const DASHBOARD_HINT = 'Problems to put right, found in your documents, incidents and inspections or added by your team.'

const MAX_REFRESH_ROWS = 500
const NESTED_DIALOG_Z_INDEX = 1400
const updateActionAsync = promisifyRoutine(updateActionRoutine)

const RemedialsWorkspace = ({ scope, compact = false, scopedBps = null }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const scopeKind = scope?.kind || 'all'
  const scopeId = scope?.id || null
  const stableScope = useMemo(() => ({ kind: scopeKind, id: scopeId }), [scopeKind, scopeId])

  const actions = useSelector(selectActions)
  const meta = useSelector(selectActionsMeta)
  const loading = useSelector(selectActionsLoading)
  const listError = useSelector(selectActionsError)
  const statsError = useSelector(selectStatsError)
  const lastMutated = useSelector(selectLastMutatedAction)
  const sections = useSelector(selectActionSections)
  const loadedStatsKey = useSelector(selectStatsKey)
  const mutationLoading = useSelector(selectMutationLoading)
  const refreshedVersion = useSelector(selectRemedialRefreshedVersion)
  const mutationError = useSelector(selectMutationError)
  const userBps = useSelector(getUserBps)

  const [selectedIds, setSelectedIds] = useState([])
  const [openSnapshot, setOpenSnapshot] = useState(null)
  const pushedOpenRef = useRef(false)
  const openSnapshotRef = useRef(null)
  openSnapshotRef.current = openSnapshot
  const routerNavigate = useNavigate()
  const [latestOpen, setLatestOpen] = useState(null)
  const [addOpen, setAddOpen] = useState(false)
  const [convertTarget, setConvertTarget] = useState(null)
  const [dismissTarget, setDismissTarget] = useState(null)
  const [completeTarget, setCompleteTarget] = useState(null)
  const [completing, setCompleting] = useState(false)
  const [bulkConvertOpen, setBulkConvertOpen] = useState(false)
  const [bulkDismissOpen, setBulkDismissOpen] = useState(false)
  const [insightFile, setInsightFile] = useState(null)

  useEffect(() => {
    rememberHubReturn(location.pathname + location.search)
  }, [location.pathname, location.search])

  useEffect(() => {
    const next = normaliseLegacyParams(searchParams)
    if (next) setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const view = readView(searchParams)
  const sort = readSort(searchParams)
  const filtersKey = JSON.stringify(readFilters(searchParams))
  const filters = useMemo(() => JSON.parse(filtersKey), [filtersKey])
  const { bp, portfolio, estate, involves } = filters

  const statsParams = useMemo(() => buildStatsParams(stableScope, { bp, portfolio, estate, involves }), [stableScope, bp, portfolio, estate, involves])
  const statsKey = paramsKey(statsParams)
  const scopedStats = useSelector(state => selectActionStatsFor(state, statsKey))
  const counts = useMemo(() => remedialViewCounts(scopedStats), [scopedStats])
  const severityCounts = useMemo(() => severityLiveCounts(scopedStats), [scopedStats])
  const countsReady = scopedStats != null || loadedStatsKey === statsKey

  const listParams = useCallback(
    (page, perPage = PAGE_SIZE) => buildListParams({ scope: stableScope, view, filters, sort, page, perPage }),
    [stableScope, view, filters, sort]
  )

  const sectionParams = useMemo(
    () => DASHBOARD_SECTIONS.map(section => ({ key: section.key, params: buildSectionParams(stableScope, { bp, portfolio, estate, involves }, section) })),
    [stableScope, bp, portfolio, estate, involves]
  )

  const loadSections = useCallback(() => {
    sectionParams.forEach(({ key, params }) => dispatch(getActionSectionRoutine({ key, params })))
  }, [dispatch, sectionParams])

  const currentSections = useMemo(() => Object.fromEntries(sectionParams.map(({ key, params }) => {
    const section = sections[key]
    const fresh = section && section.requestKey === paramsKey(params)
    return [key, fresh ? section : { ...EMPTY_SECTION, loading: true }]
  })), [sectionParams, sections])

  useEffect(() => {
    dispatch(getActionStatsRoutine(statsParams))
  }, [dispatch, statsParams])

  useEffect(() => {
    setSelectedIds([])
    if (view === DASHBOARD) loadSections()
    else dispatch(getActionsRoutine(listParams(1)))
  }, [dispatch, view, loadSections, listParams])

  const loadedCount = actions.length
  const refreshAll = useCallback(() => {
    dispatch(getActionStatsRoutine(statsParams))
    if (view === DASHBOARD) loadSections()
    else dispatch(refreshActionsRoutine(listParams(1, Math.min(Math.max(PAGE_SIZE, loadedCount), MAX_REFRESH_ROWS))))
  }, [dispatch, statsParams, view, loadSections, listParams, loadedCount])

  const wasMutating = useRef(false)
  const pendingBulkClear = useRef(false)
  useEffect(() => {
    if (wasMutating.current && !mutationLoading) {
      if (pendingBulkClear.current && !mutationError) setSelectedIds([])
      pendingBulkClear.current = false
      refreshAll()
    }
    wasMutating.current = mutationLoading
  }, [mutationLoading, mutationError, refreshAll])

  const seenRefreshedVersion = useRef(refreshedVersion)
  useEffect(() => {
    if (seenRefreshedVersion.current === refreshedVersion) return
    seenRefreshedVersion.current = refreshedVersion
    refreshAll()
  }, [refreshedVersion, refreshAll])

  useEffect(() => {
    setSelectedIds(prev => {
      if (!prev.length) return prev
      const next = prev.filter(id => actions.some(a => a.id === id))
      return next.length === prev.length ? prev : next
    })
  }, [actions])

  const loadMore = useCallback(() => {
    if (!meta || meta.currentPage >= meta.lastPage) return
    dispatch(getActionsRoutine(listParams(meta.currentPage + 1, meta.perPage || PAGE_SIZE)))
  }, [dispatch, meta, listParams])

  const setParams = useCallback(changes => {
    setSearchParams(prev => withParams(prev, changes), { replace: true })
  }, [setSearchParams])

  const openView = useCallback(({ view: nextView, severity = null }) => {
    setSearchParams(prev => withParams(prev, { ...VIEW_RESET, view: nextView, severity }))
  }, [setSearchParams])

  const selectView = useCallback(key => openView({ view: key }), [openView])
  const changeFilters = useCallback(changes => setParams(changes), [setParams])
  const clearFilters = useCallback(() => setParams(FILTER_RESET), [setParams])
  const changeSort = useCallback(column => setParams({ sort: nextSort(sort, column) }), [setParams, sort])
  const chooseSort = useCallback(value => setParams({ sort: value }), [setParams])

  const changeOwner = useCallback((action, ownerUserId) => {
    dispatch(updateActionRoutine({ id: action.id, ownerUserId }))
  }, [dispatch])
  const changeDue = useCallback((action, dueDate) => {
    dispatch(updateActionRoutine({ id: action.id, dueDate }))
  }, [dispatch])
  const changeSeverity = useCallback((action, severity) => updateActionAsync({ id: action.id, severity }, dispatch), [dispatch])
  const bulkOwner = useCallback(person => {
    pendingBulkClear.current = true
    dispatch(bulkUpdateActionsRoutine({ ids: selectedIds, ownerUserId: person.id }))
  }, [dispatch, selectedIds])
  const bulkDue = useCallback(date => {
    pendingBulkClear.current = true
    dispatch(bulkUpdateActionsRoutine({ ids: selectedIds, dueDate: date }))
  }, [dispatch, selectedIds])

  const confirmComplete = useCallback(async () => {
    if (!completeTarget || completing) return
    setCompleting(true)
    try {
      const response = await ActionsAPI.resolve(completeTarget.id)
      const resolved = response?.data?.data
      if (resolved?.id) setLatestOpen(prev => (prev?.id === resolved.id ? mergeAction(prev, resolved) : prev))
      toast.success('Remedial marked complete.')
      setCompleteTarget(null)
      refreshAll()
    } catch (e) {
      toast.error('Could not mark this remedial complete. Please try again.')
    } finally {
      setCompleting(false)
    }
  }, [completeTarget, completing, refreshAll])

  const openSource = useCallback(action => {
    if (!action.sourceFile?.id || !action.buildingPassport?.id) return
    setInsightFile({
      id: action.sourceFile.id,
      displayName: action.sourceFile.displayName,
      buildingPassportId: action.buildingPassport.id,
      buildingPassportName: action.buildingPassport.name,
    })
  }, [])

  const showRemedial = useCallback((action, focus) => {
    setOpenSnapshot(action)
    setLatestOpen(action)
    pushedOpenRef.current = true
    setSearchParams(prev => withParams(prev, { open: action.id, focus }))
  }, [setSearchParams])
  const openRemedial = useCallback(action => showRemedial(action, null), [showRemedial])
  const openRemedialComments = useCallback(action => showRemedial(action, 'comments'), [showRemedial])
  const clearOpenParam = useCallback(() => {
    if (pushedOpenRef.current && window.history.length > 1) {
      pushedOpenRef.current = false
      routerNavigate(-1)
      return
    }
    pushedOpenRef.current = false
    setSearchParams(prev => withParams(prev, { open: null, focus: null }), { replace: true })
  }, [routerNavigate, setSearchParams])
  const closeDrawer = useCallback(() => { setOpenSnapshot(null); clearOpenParam() }, [clearOpenParam])
  const convertFromDrawer = useCallback(action => { setOpenSnapshot(null); clearOpenParam(); setConvertTarget(action) }, [clearOpenParam])

  const openParam = searchParams.get('open')
  const drawerFocus = openParam && searchParams.get('focus') === 'comments' ? 'comments' : null
  useEffect(() => {
    if (!openParam) {
      pushedOpenRef.current = false
      setOpenSnapshot(prev => (prev ? null : prev))
      return undefined
    }
    if (openSnapshotRef.current?.id === openParam) return undefined
    const known = actions.find(a => a.id === openParam)
      || Object.values(sections).flatMap(sec => sec.items || []).find(a => a.id === openParam)
    if (known) {
      setOpenSnapshot(known)
      setLatestOpen(known)
      return undefined
    }
    let cancelled = false
    ActionsAPI.get(openParam)
      .then(res => {
        const loaded = res?.data?.data
        if (!cancelled && loaded) {
          setOpenSnapshot(loaded)
          setLatestOpen(loaded)
        }
      })
      .catch(() => {
        if (!cancelled) setSearchParams(prev => withParams(prev, { open: null, focus: null }), { replace: true })
      })
    return () => { cancelled = true }
  }, [openParam, actions, sections, setSearchParams])
  const closeInsight = useCallback(() => setInsightFile(null), [])
  const openBulkConvert = useCallback(() => setBulkConvertOpen(true), [])
  const openBulkDismiss = useCallback(() => setBulkDismissOpen(true), [])

  const openId = openSnapshot?.id || null
  const liveOpen = useMemo(() => {
    if (!openId) return null
    const fromList = actions.find(a => a.id === openId)
    if (fromList) return fromList
    return Object.values(sections).flatMap(s => s.items || []).find(a => a.id === openId) || null
  }, [openId, actions, sections])

  useEffect(() => {
    if (liveOpen) setLatestOpen(prev => (prev === liveOpen ? prev : liveOpen))
  }, [liveOpen])

  useEffect(() => {
    const snapshot = openSnapshotRef.current
    if (!snapshot || !lastMutated || lastMutated.id !== snapshot.id) return
    setLatestOpen(prev => mergeAction(prev?.id === snapshot.id ? prev : snapshot, lastMutated))
  }, [lastMutated])

  const openAction = useMemo(() => {
    if (!openSnapshot) return null
    if (liveOpen) return liveOpen
    if (latestOpen?.id === openSnapshot.id) return latestOpen
    return openSnapshot
  }, [openSnapshot, liveOpen, latestOpen])

  const retryStats = useCallback(() => dispatch(getActionStatsRoutine(statsParams)), [dispatch, statsParams])
  const retrySection = useCallback(key => {
    const entry = sectionParams.find(item => item.key === key)
    if (entry) dispatch(getActionSectionRoutine({ key: entry.key, params: entry.params }))
  }, [dispatch, sectionParams])
  const retryList = useCallback(() => {
    if (actions.length === 0) dispatch(getActionsRoutine(listParams(1)))
    else dispatch(refreshActionsRoutine(listParams(1, Math.min(Math.max(PAGE_SIZE, actions.length), MAX_REFRESH_ROWS))))
  }, [dispatch, actions.length, listParams])

  const chooseScope = useCallback((key, value) => {
    const others = { bp: ['portfolio', 'estate'], portfolio: ['bp', 'estate'], estate: ['bp', 'portfolio'] }[key]
    const changes = { [key]: value || null }
    if (value) others.forEach(other => { changes[other] = null })
    setParams(changes)
  }, [setParams])
  const chooseEstate = useCallback(estateId => chooseScope('estate', estateId), [chooseScope])

  const addableBps = useMemo(() => {
    if (scopeKind === 'building') return []
    const pool = Array.isArray(scopedBps) && scopedBps.length
      ? scopedBps.filter(b => b.hasTaskManagement !== false)
      : (Array.isArray(userBps) ? userBps : []).filter(b => b.hasTaskManagement)
    return pool.map(b => ({ id: b.id, name: b.name || b.address || 'Building Passport' }))
  }, [scopeKind, scopedBps, userBps])

  const canAdd = scopeKind === 'building' || addableBps.length > 0
  const openAdd = useCallback(() => setAddOpen(true), [])
  const buildingId = scopeKind === 'building' ? scopeId : (bp || null)
  const showBuilding = scopeKind !== 'building' && !bp
  const showReviewEntry = scopeKind === 'all' || Boolean(buildingId)
  const ownerName = useMemo(
    () => (filters.owner ? actions.find(a => a.ownerUserId === filters.owner)?.owner?.name || null : null),
    [filters.owner, actions]
  )

  const isDashboard = view === DASHBOARD
  const def = isDashboard ? null : viewDef(view)
  const viewTitle = isDashboard ? 'Dashboard' : def.label
  const viewHint = isDashboard ? DASHBOARD_HINT : def.hint
  const listTotal = !isDashboard && meta && !(loading && actions.length === 0) ? meta.total ?? null : null

  const scopeFilters = scopeKind === 'building' ? null : (
    <>
      <ScopePickers
        bp={bp}
        portfolio={portfolio}
        onChoose={chooseScope}
        scopedBps={scopedBps}
        showPortfolio={scopeKind === 'all'}
      />
      {scopeKind === 'all' && <EstateFilter value={estate} onChange={chooseEstate} />}
    </>
  )

  const sidebarProps = { view, counts, countsReady, statsError: statsError && !countsReady, onRetry: retryStats, onSelect: selectView, compact }
  const railTop = scopeKind === 'all' ? HUB_RAIL_TOP : undefined

  return (
    <Page $compact={compact}>
      <Layout $compact={compact}>
        <ViewSidebar {...sidebarProps} mode='rail' stickyTop={railTop} />
        <Main>
          <Header>
            <HeaderText>
              <TitleLine>
                {!compact && <Title>Remedials</Title>}
                {!compact && <Crumb aria-hidden='true'>/</Crumb>}
                <ViewTitle>{viewTitle}</ViewTitle>
                {listTotal !== null && (
                  <TotalBadge title='Remedials matching this view and your filters' data-tour-id='remedials.total-badge'>{listTotal}</TotalBadge>
                )}
                <TasksRemedialsExplainer />
              </TitleLine>
              <Lead>
                <span>{viewHint}</span>
                <Explainers>
                  <StageExplainer text='What do the stages mean?' />
                  <OverdueExplainer text='When is a remedial overdue?' />
                </Explainers>
                <MobileHelp>
                  <RemedialsHelp />
                </MobileHelp>
              </Lead>
            </HeaderText>
            {canAdd && (
              <AddButton type='button' onClick={openAdd} aria-label='Add remedial'>
                <AddIcon style={{ fontSize: 20 }} aria-hidden='true' />
                <FullLabel>Add remedial</FullLabel>
                <ShortLabel>Add</ShortLabel>
              </AddButton>
            )}
          </Header>

          {(isDashboard || compact) && <ViewSidebar {...sidebarProps} mode='picker' hideOnMobile={!isDashboard} />}

          <Notices>
            {showReviewEntry && <ReviewSlot><RemedialsReviewEntry bpId={buildingId} /></ReviewSlot>}
            <GuruSlot><GuruTip storageKey='guru_tip_remedials' noun='remedial' prompt='Raise a remedial: ' /></GuruSlot>
          </Notices>

          {isDashboard ? (
            <>
              {(scopeFilters || involves) && (
                <ScopeBar role='group' aria-label='Show remedials for'>
                  <ScopeLabel>Show remedials for</ScopeLabel>
                  {scopeFilters}
                  <InvolvesChip filters={filters} onChange={changeFilters} />
                </ScopeBar>
              )}
              <DashboardView
                counts={counts}
                severityCounts={severityCounts}
                ready={countsReady}
                statsError={statsError && !countsReady}
                onRetryStats={retryStats}
                onRetrySection={retrySection}
                sections={currentSections}
                onOpenView={openView}
                onOpenRemedial={openRemedial}
                showBuilding={showBuilding}
              />
            </>
          ) : (
            <TableView
              view={view}
              filters={filters}
              sort={sort}
              actions={actions}
              meta={meta}
              loading={loading}
              error={!!listError}
              onRetry={retryList}
              selectedIds={selectedIds}
              ownerName={ownerName}
              buildingId={buildingId}
              showBuilding={showBuilding}
              scopeFilters={scopeFilters}
              viewPicker={<ViewSidebar {...sidebarProps} mode='inline' />}
              onFiltersChange={changeFilters}
              onClearFilters={clearFilters}
              onSort={changeSort}
              onSortChoose={chooseSort}
              onLoadMore={loadMore}
              onSelect={setSelectedIds}
              onOpen={openRemedial}
              onAdd={canAdd ? openAdd : undefined}
              onCreateTask={setConvertTarget}
              onComplete={setCompleteTarget}
              onDismiss={setDismissTarget}
              onChangeOwner={changeOwner}
              onChangeDue={changeDue}
              onChangeSeverity={changeSeverity}
              onOpenSource={openSource}
              onOpenComments={openRemedialComments}
              onBulkConvert={openBulkConvert}
              onBulkDismiss={openBulkDismiss}
              onBulkOwner={bulkOwner}
              onBulkDue={bulkDue}
            />
          )}
        </Main>
      </Layout>

      <ActionDetailDrawer
        action={openAction}
        open={!!openAction}
        focus={drawerFocus}
        onClose={closeDrawer}
        onConvert={convertFromDrawer}
        onComplete={setCompleteTarget}
        onOpenSource={openSource}
      />
      <AddActionDialog open={addOpen} onClose={() => setAddOpen(false)} buildingPassportId={buildingId} availableBps={addableBps} />
      <ConvertToTaskDialog action={convertTarget} open={!!convertTarget} onClose={() => setConvertTarget(null)} zIndex={1400} />
      <DismissActionDialog action={dismissTarget} open={!!dismissTarget} onClose={() => setDismissTarget(null)} onDismissed={() => setDismissTarget(null)} />
      <CompleteActionDialog
        action={completeTarget}
        open={!!completeTarget}
        onClose={() => setCompleteTarget(null)}
        submitting={completing}
        onConfirm={confirmComplete}
        zIndex={NESTED_DIALOG_Z_INDEX}
      />
      <BulkConvertDialog
        ids={selectedIds}
        open={bulkConvertOpen}
        onClose={() => setBulkConvertOpen(false)}
        onConverted={() => { setBulkConvertOpen(false); setSelectedIds([]) }}
      />
      <BulkDismissDialog
        ids={selectedIds}
        open={bulkDismissOpen}
        onClose={() => setBulkDismissOpen(false)}
        onDismissed={() => { setBulkDismissOpen(false); setSelectedIds([]) }}
      />
      {insightFile && (
        <GuruInsightModal
          open
          onClose={closeInsight}
          onUpdate={refreshAll}
          file={insightFile}
          bpId={insightFile.buildingPassportId}
          buildingPassportName={insightFile.buildingPassportName}
        />
      )}
    </Page>
  )
}

export default RemedialsWorkspace

const Page = styled.div`
  max-width: ${({ $compact }) => ($compact ? 'none' : '1440px')};
  margin: 0 auto;
  padding: ${({ $compact }) => ($compact ? '0' : '16px 24px 24px')};
  font-size: 15px;
  @media (max-width: 768px) { padding: ${({ $compact }) => ($compact ? '0' : '12px 16px 16px')}; }
`

const Layout = styled.div`
  display: grid;
  grid-template-columns: ${({ $compact }) => ($compact ? 'minmax(0, 1fr)' : 'auto minmax(0, 1fr)')};
  gap: 16px;
  align-items: start;
  @media (max-width: 768px) { grid-template-columns: minmax(0, 1fr); }
`

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 16px;
  flex-wrap: wrap;
  @media (max-width: 768px) { flex-wrap: nowrap; gap: 8px; }
`

const AddButton = styled(PrimaryButton)`
  flex-shrink: 0;
  @media (max-width: 768px) { padding: 0 14px; }
`

const FullLabel = styled.span`
  @media (max-width: 768px) { display: none; }
`

const ShortLabel = styled.span`
  display: none;
  @media (max-width: 768px) { display: inline; }
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 420px;
  @media (max-width: 768px) { flex: 1 1 0; }
`

const TitleLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
`

const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  @media (max-width: 768px) { font-size: 18px; }
  font-weight: 700;
  color: ${INK};
`

const Crumb = styled.span`
  font-size: 20px;
  @media (max-width: 768px) { font-size: 16px; }
  color: #a1a1aa;
`

const ViewTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  @media (max-width: 768px) { font-size: 18px; }
  font-weight: 600;
  color: ${INK};
`

const TotalBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  background: #f4f4f5;
  font-size: 14px;
  font-weight: 700;
  color: ${MUTED};
  font-variant-numeric: tabular-nums;
`

const Lead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 8px;
  font-size: 15px;
  line-height: 1.45;
  color: ${MUTED};
  @media (max-width: 768px) { font-size: 14px; }
`

const Explainers = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0 4px;
  margin-left: -6px;
  @media (max-width: 768px) { display: none; }
`

const MobileHelp = styled.span`
  display: none;
  @media (max-width: 768px) { display: inline-flex; margin-left: -6px; }
`

const Notices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  &:not(:has(> div:not(:empty))) { display: none; }
  @media (max-width: 768px) {
    & > div > * { margin-bottom: 0; }
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
`

const ReviewSlot = styled.div`
  min-width: 0;
  &:empty { display: none; }
  @media (max-width: 768px) {
    flex: 1 1 0;
    & > button {
      min-height: 40px;
      padding: 4px 10px;
      gap: 6px;
      font-size: 14px;
    }
    & > button > span:first-child { display: none; }
    & > button > span:nth-child(2) > span:first-child {
      font-size: 14px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    & > button > span:nth-child(2) > span:nth-child(2) { display: none; }
  }
`

const GuruSlot = styled.div`
  min-width: 0;
  &:empty { display: none; }
  @media (max-width: 768px) {
    flex: 0 0 auto;
    margin-left: auto;
    & > [role='note'] { padding: 0 0 0 4px; gap: 0; }
    & > [role='note'] > span:first-child, & > [role='note'] > div { display: none; }
    & > [role='note'] > button { min-height: 40px; }
    & > [role='note'] > button[aria-label='Dismiss'] { min-width: 40px; }
  }
`

const ScopeBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  &:not(:has(button)) { display: none; }
  @media (max-width: 768px) { & > button { flex: 1 1 100%; max-width: none; } }
`

const ScopeLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${MUTED};
`
